import { useEffect, useRef, useState, useCallback } from "react";
import PaymentSkeleton from "../skeleton/payment";
import {
  searchPlaces,
  getPlaceDetails,
  logApiError,
} from "../../services/googlePlaces.service";

const LocationSearch = ({
  onPlaceSelect,
  defaultAddress,
  defaultAddressTitle,
  searchLocationTitle,
}) => {
  const resultsRef = useRef(null);
  const [isEditing, setIsEditing] = useState(!defaultAddress);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);

  // Re-sync state if defaultAddress changes later (e.g., after fetch)
  useEffect(() => {
    if (defaultAddress) {
      setIsEditing(false);
    }
  }, [defaultAddress]);

  const extract = useCallback((place, displayName = null) => {
    // Handle both old Google Maps SDK format and new REST API format
    const comp = place.addressComponents || [];

    const get = (types) => {
      const component = comp.find((c) =>
        c.types.some((t) => types.includes(t))
      );
      // Handle both old format (longText) and new format (longName)
      return component?.longText ?? component?.longName ?? "";
    };

    const locality = get(["locality", "administrative_area_level_2"]);
    const admin1 = get(["administrative_area_level_1"]);
    const country = get(["country"]);
    const postalCode = get(["postal_code"]);

    const address = place.formattedAddress;

    // Handle both old format (lat()/lng()) and new format (latitude/longitude)
    let lat, lng;
    if (place.location) {
      if (typeof place.location.lat === "function") {
        // Old Google Maps SDK format
        lat = String(place.location.lat());
        lng = String(place.location.lng());
      } else {
        // New REST API format
        lat = String(place.location.latitude || "");
        lng = String(place.location.longitude || "");
      }
    } else {
      lat = "";
      lng = "";
    }

    // Use displayName (user-friendly name) for search value if provided
    // Otherwise fall back to place name or formatted address
    const displayValue =
      displayName || place.displayName?.text || place.name || address;
    setSearchValue(displayValue);

    return {
      address,
      country,
      city: locality,
      state: admin1,
      postalCode,
      lat,
      lng,
    };
  }, []);

  useEffect(() => {
    if (!isEditing) return;

    async function init() {
      const resultsElement = resultsRef.current;
      const textInput = document.getElementById("text-input");
      if (!textInput) return;

      let debounceTimer = null;

      textInput.addEventListener("input", () => {
        const query = textInput.value.trim();

        if (query.length <= 3 && resultsElement) {
          resultsElement.innerHTML = "";
          return;
        }

        // debounce to prevent spamming the API
        clearTimeout(debounceTimer);

        debounceTimer = setTimeout(() => {
          findPlaces(query);
        }, 300); // maintain 300ms delay per requirements 3.4, 4.4
      });
    }

    async function findPlaces(query) {
      if (loading) return; // prevent overlapping calls

      setLoading(true);

      try {
        // Use new searchPlaces function with dual API strategy
        const response = await searchPlaces(query);
        const predictions = response.predictions || [];

        const resultsElement = resultsRef.current;
        if (resultsElement) {
          resultsElement.innerHTML = "";
        }

        for (let prediction of predictions) {
          if (resultsElement) {
            const listItem = document.createElement("li");
            listItem.textContent = prediction.description;
            listItem.classList.add("px-4", "cursor-pointer", "hover:bg-gray");

            listItem.addEventListener("click", async () => {
              try {
                let placeDetails;

                // If we have fullData from searchText API, use it directly
                if (prediction.fullData) {
                  placeDetails = prediction.fullData;
                } else {
                  // Otherwise, fetch place details using the place_id
                  placeDetails = await getPlaceDetails(prediction.place_id);
                }

                if (!placeDetails) {
                  resultsElement.innerHTML = "";
                  return;
                }

                resultsElement.innerHTML = "";
                // Pass the user-friendly description as display name
                const extractedData = extract(
                  placeDetails,
                  prediction.description
                );
                onPlaceSelect(extractedData);
              } catch (error) {
                logApiError("getPlaceDetails", error, {
                  placeId: prediction.place_id,
                });
                // Still clear results even if details fetch fails
                resultsElement.innerHTML = "";
              }
            });

            resultsElement.appendChild(listItem);
          }
        }
      } catch (err) {
        logApiError("searchPlaces", err, { query });
        console.error("Places search failed:", err);
      } finally {
        setLoading(false);
      }
    }

    init();

    // eslint-disable-next-line
  }, [isEditing, extract]);

  // UI: Saved address + Change
  if (!isEditing && defaultAddress) {
    return (
      <div>
        <label className="block mb-2 text-sm font-medium text-[#151515] dark:text-white">
          {defaultAddressTitle ?? "Address"}
        </label>
        <div className="flex items-center justify-between sm:text-sm h-[40px] px-4 py-2 border border-gray-300 w-full text-base bg-[#FFFFFF] dark:bg-[transparent]">
          <span>{defaultAddress}</span>
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="text-primary text-sm font-medium hover:underline"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  // UI: Search box (Create OR Edit after Change)
  return (
    <div>
      <label className="block mb-2 text-sm font-medium text-[#151515] dark:text-white">
        {searchLocationTitle ?? "Search for a Location"}
      </label>

      <div className="relative">
        <div id="text-input-card">
          <input
            type="search"
            id="text-input"
            autoComplete="off"
            autoFocus="off"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search for a place"
            className="sm:text-sm min-h-[40px] w-full px-4 border border-gray-300 text-base bg-[#FFFFFF] dark:bg-[transparent] focus:ring-2 focus:ring-red-600 focus:border-red-600 focus:outline-none"
          />
        </div>

        <div className="bg-white dark:bg-black">
          {loading && <PaymentSkeleton />}
          <ul
            ref={resultsRef}
            id="google_suggestions"
            className="flex flex-col gap-4 w-full absolute left-0 right-0 bg-white dark:bg-black z-10 shadow shadow-lg"
          />
        </div>
      </div>

      {defaultAddress && isEditing && (
        <div className="flex justify-end mt-1">
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="text-sm text-gray-500 hover:underline"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
